import { useCallback, useEffect, useRef, useState } from "react";

/** Margen para considerar que el usuario está "abajo del todo". */
const THRESHOLD_PX = 72;

/**
 * Mantiene el hilo pegado abajo MIENTRAS el usuario quiera.
 *
 * Lo que había antes era `scrollIntoView({behavior:"smooth"})` en cada token.
 * Eso hacía dos cosas mal: encolaba una animación por token, que es lo que se
 * veía como saltos fantasma, y arrastraba al usuario abajo aunque hubiera
 * subido a leer algo.
 *
 * Aquí:
 *  - solo se sigue el final si el usuario YA estaba abajo;
 *  - si sube, se le deja en paz y se le ofrece un botón para volver;
 *  - durante el streaming el desplazamiento es instantáneo, porque animar cada
 *    fragmento es justo lo que produce el temblor.
 */
export const useStickToBottom = <T extends HTMLElement>(
  dependency: unknown,
): {
  containerRef: React.RefObject<T | null>;
  atBottom: boolean;
  scrollToBottom: (smooth?: boolean) => void;
} => {
  const containerRef = useRef<T>(null);
  const [atBottom, setAtBottom] = useState(true);
  // En una ref además del estado: el efecto de scroll no debe re-suscribirse
  // en cada render, y leer el valor fresco desde ahí evita depender de él.
  const stuck = useRef(true);

  const scrollToBottom = useCallback((smooth = false): void => {
    const element = containerRef.current;
    if (element === null) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    element.scrollTo({
      top: element.scrollHeight,
      behavior: smooth && !reduced ? "smooth" : "auto",
    });
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (element === null) return;

    const onScroll = (): void => {
      const distance =
        element.scrollHeight - element.scrollTop - element.clientHeight;
      const isAtBottom = distance <= THRESHOLD_PX;
      stuck.current = isAtBottom;
      setAtBottom(isAtBottom);
    };

    element.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      element.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!stuck.current) return;
    // Instantáneo: durante el streaming esto corre muchas veces seguidas.
    scrollToBottom(false);
  }, [dependency, scrollToBottom]);

  return { containerRef, atBottom, scrollToBottom };
};
